"use client";

import { useState } from "react";
import { renderSlots } from "@/components/tools/template-slots";
import { useToolUi } from "@/components/tools/tool-ui";
import { parseIntegerInput } from "@/lib/parse-number";

const RE_TIME_24 = /^([01]?\d|2[0-3]):?([0-5]\d)$/;

type Meridiem = "AM" | "PM";

function pad2(n: number): string {
  return String(n).padStart(2, "0");
}

function to24Hour(hour12: number, minute: number, meridiem: Meridiem): string {
  let hour = hour12 % 12;
  if (meridiem === "PM") {
    hour += 12;
  }
  return `${pad2(hour)}:${pad2(minute)}`;
}

type Strings = { [K in keyof typeof MILITARY_TIME_CONVERTER_STRINGS]: string };

function to12Hour(hour24: number, minute: number, strings: Strings): string {
  const meridiem = hour24 < 12 ? strings.am : strings.pm;
  let hour = hour24 % 12;
  if (hour === 0) {
    hour = 12;
  }
  return `${hour}:${pad2(minute)} ${meridiem}`;
}

const SPOKEN_HOURS = [
  "zero hundred",
  "zero one hundred",
  "zero two hundred",
  "zero three hundred",
  "zero four hundred",
  "zero five hundred",
  "zero six hundred",
  "zero seven hundred",
  "zero eight hundred",
  "zero nine hundred",
  "ten hundred",
  "eleven hundred",
  "twelve hundred",
  "thirteen hundred",
  "fourteen hundred",
  "fifteen hundred",
  "sixteen hundred",
  "seventeen hundred",
  "eighteen hundred",
  "nineteen hundred",
  "twenty hundred",
  "twenty-one hundred",
  "twenty-two hundred",
  "twenty-three hundred",
];

function chartRows(strings: Strings) {
  return Array.from({ length: 24 }, (_, hour) => ({
    hour,
    military: `${pad2(hour)}:00`,
    twelveHour: to12Hour(hour, 0, strings),
    spoken: SPOKEN_HOURS[hour],
  }));
}

/** English strings. A translated content file overrides these through `ui`. */
export const MILITARY_TIME_CONVERTER_STRINGS = {
  twelveToMilitaryHeading: "12-hour to military time",
  hourLabel: "Hour (1–12)",
  minuteLabel: "Minute",
  amPmLabel: "AM / PM",
  am: "AM",
  pm: "PM",
  militaryTimeLabel: "Military time",
  invalidTwelveHourError:
    "Enter an hour from 1 to 12 and a minute from 0 to 59.",
  militaryToTwelveHeading: "Military time to 12-hour",
  twentyFourHourLabel: "24-hour time (e.g. 1730 or 17:30)",
  twentyFourPlaceholder: "1730",
  invalid24HourError:
    "Enter a valid 24-hour time between 00:00 and 23:59, with or without the colon.",
  twelveHourTimeLabel: "12-hour time",
  fullConversionChart: "Full conversion chart",
  howToSayItColumn: "How to say it",
  howToSayMilitaryTimeHeading: "How to say military time",
  paragraph1:
    'Read the four digits as hours, then minutes. On-the-hour times end in "hundred": 0900 is {zeroNineHundred}, 1300 is {thirteenHundred}. The word "hours" is often added afterwards, as in "zero nine hundred hours".',
  paragraph1SpokenZeroNineHundred: "zero nine hundred",
  paragraph1SpokenThirteenHundred: "thirteen hundred",
  paragraph2:
    'With minutes, just read both pairs: 0915 is {zeroNineFifteen}, 2145 is {twentyOneFortyFive}. In strict military radio usage each digit can be spoken individually, and 0 is read as "zero", never "oh".',
  paragraph2SpokenZeroNineFifteen: "zero nine fifteen",
  paragraph2SpokenTwentyOneFortyFive: "twenty-one forty-five",
} as const;

export default function MilitaryTimeConverterWidget() {
  const [hour12, setHour12] = useState("9");
  const [minute12, setMinute12] = useState("00");
  const [meridiem, setMeridiem] = useState<Meridiem>("AM");
  const [time24, setTime24] = useState("");

  const strings = useToolUi(MILITARY_TIME_CONVERTER_STRINGS);

  const hour12Num = parseIntegerInput(hour12);
  const minute12Num = parseIntegerInput(minute12);
  const twelveValid =
    hour12Num !== null &&
    minute12Num !== null &&
    hour12Num >= 1 &&
    hour12Num <= 12 &&
    minute12Num >= 0 &&
    minute12Num <= 59;
  const militaryResult =
    hour12Num !== null && minute12Num !== null && twelveValid
      ? to24Hour(hour12Num, minute12Num, meridiem)
      : null;

  const match24 = RE_TIME_24.exec(time24.trim());
  const twelveResult =
    match24 === null
      ? null
      : to12Hour(
          Number.parseInt(match24[1], 10),
          Number.parseInt(match24[2], 10),
          strings
        );
  const show24Error = time24.trim().length > 0 && match24 === null;

  return (
    <>
      {/* 12h -> 24h */}
      <section className="space-y-3">
        <h2 className="font-semibold text-foreground text-xl">
          {strings.twelveToMilitaryHeading}
        </h2>
        <div className="flex flex-wrap items-end gap-3">
          <div className="space-y-2">
            <label
              className="block font-medium text-foreground text-sm"
              htmlFor="hour-12"
            >
              {strings.hourLabel}
            </label>
            <input
              className="w-24 rounded-lg border border-border bg-card p-3 font-mono text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              id="hour-12"
              inputMode="numeric"
              max={12}
              min={1}
              onChange={(e) => setHour12(e.target.value)}
              type="number"
              value={hour12}
            />
          </div>
          <div className="space-y-2">
            <label
              className="block font-medium text-foreground text-sm"
              htmlFor="minute-12"
            >
              {strings.minuteLabel}
            </label>
            <input
              className="w-24 rounded-lg border border-border bg-card p-3 font-mono text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              id="minute-12"
              inputMode="numeric"
              max={59}
              min={0}
              onChange={(e) => setMinute12(e.target.value)}
              type="number"
              value={minute12}
            />
          </div>
          <div className="space-y-2">
            <label
              className="block font-medium text-foreground text-sm"
              htmlFor="meridiem"
            >
              {strings.amPmLabel}
            </label>
            <select
              className="rounded-lg border border-border bg-card p-3 text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              id="meridiem"
              onChange={(e) => setMeridiem(e.target.value as Meridiem)}
              value={meridiem}
            >
              <option value="AM">{strings.am}</option>
              <option value="PM">{strings.pm}</option>
            </select>
          </div>
        </div>
        {militaryResult !== null && (
          <div className="rounded-lg border border-border bg-muted/30 p-4">
            <p className="text-muted-foreground text-xs">
              {strings.militaryTimeLabel}
            </p>
            <p className="font-mono font-semibold text-2xl text-foreground">
              {militaryResult}
            </p>
          </div>
        )}
        {!twelveValid && (
          <p className="rounded-lg border border-destructive/40 bg-destructive/5 p-4 text-destructive text-sm">
            {strings.invalidTwelveHourError}
          </p>
        )}
      </section>

      {/* 24h -> 12h */}
      <section className="space-y-3">
        <h2 className="font-semibold text-foreground text-xl">
          {strings.militaryToTwelveHeading}
        </h2>
        <div className="space-y-2">
          <label
            className="font-medium text-foreground text-sm"
            htmlFor="time-24"
          >
            {strings.twentyFourHourLabel}
          </label>
          <input
            className="w-full rounded-lg border border-border bg-card p-3 font-mono text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            id="time-24"
            onChange={(e) => setTime24(e.target.value)}
            placeholder={strings.twentyFourPlaceholder}
            type="text"
            value={time24}
          />
        </div>
        {show24Error && (
          <p className="rounded-lg border border-destructive/40 bg-destructive/5 p-4 text-destructive text-sm">
            {strings.invalid24HourError}
          </p>
        )}
        {twelveResult !== null && (
          <div className="rounded-lg border border-border bg-muted/30 p-4">
            <p className="text-muted-foreground text-xs">
              {strings.twelveHourTimeLabel}
            </p>
            <p className="font-mono font-semibold text-2xl text-foreground">
              {twelveResult}
            </p>
          </div>
        )}
      </section>

      {/* Chart */}
      <section className="space-y-3">
        <h2 className="font-semibold text-foreground text-xl">
          {strings.fullConversionChart}
        </h2>
        <div className="overflow-x-auto rounded-lg border border-border">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-border border-b bg-muted/30">
                <th className="p-3 font-medium text-foreground">
                  {strings.militaryTimeLabel}
                </th>
                <th className="p-3 font-medium text-foreground">
                  {strings.twelveHourTimeLabel}
                </th>
                <th className="p-3 font-medium text-foreground">
                  {strings.howToSayItColumn}
                </th>
              </tr>
            </thead>
            <tbody>
              {chartRows(strings).map((row) => (
                <tr
                  className="border-border border-b last:border-b-0"
                  key={row.hour}
                >
                  <td className="p-3 font-mono text-foreground">
                    {row.military}
                  </td>
                  <td className="p-3 text-muted-foreground">
                    {row.twelveHour}
                  </td>
                  <td className="p-3 text-muted-foreground">{row.spoken}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* How to say it */}
      <section className="space-y-4">
        <h2 className="font-semibold text-foreground text-xl">
          {strings.howToSayMilitaryTimeHeading}
        </h2>
        <div className="space-y-3 text-muted-foreground text-sm leading-relaxed">
          <p>
            {renderSlots(strings.paragraph1, {
              zeroNineHundred: (
                <span className="font-medium text-foreground">
                  {strings.paragraph1SpokenZeroNineHundred}
                </span>
              ),
              thirteenHundred: (
                <span className="font-medium text-foreground">
                  {strings.paragraph1SpokenThirteenHundred}
                </span>
              ),
            })}
          </p>
          <p>
            {renderSlots(strings.paragraph2, {
              zeroNineFifteen: (
                <span className="font-medium text-foreground">
                  {strings.paragraph2SpokenZeroNineFifteen}
                </span>
              ),
              twentyOneFortyFive: (
                <span className="font-medium text-foreground">
                  {strings.paragraph2SpokenTwentyOneFortyFive}
                </span>
              ),
            })}
          </p>
        </div>
      </section>
    </>
  );
}
